// SettingsCollectionsPane.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { buildBaseDatabases } from 'main/sidebar/buckets/utils/bucketTransforms';
import { subscribeToCollectionsShared } from 'services/collections';
import { NODE_MODULE } from 'utilities/nodeModule';
import { isSystemTypeData } from 'security/typeAccess';

// CSS
import './SettingsCollectionsPane.css';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import Icon from 'svg/Icon';
import DatabaseModal from 'collection/settings/DatabaseModal';
import useOpenDatabaseSettingsModal from 'collection/hooks/useOpenDatabaseSettingsModal';
import useSystemCollectionPurge from 'collection/hooks/useSystemCollectionPurge';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';

/**
 * SettingsCollectionsPane
 *
 * Lists every base collection (including non–top-level system types) with
 * settings (and system purge) controls.
 *
 * @param {object|null} [base] - Active base.
 * @param {object} theme - Theme tokens.
 *
 * @returns {ReactNode}
 */
const SettingsCollectionsPane = React.memo(({
  base = null,
  theme,
}) => {
  const language = useCurrentLanguage();
  const { getType, getRevision } = useNodeTypes();
  const nodeTypesTick = getRevision();

  const [databases, setDatabases] = useState([]);

  const baseKey = String(base?.key || '').trim();

  const {
    canOpenSettingsForType,
    databaseModalProps,
    isOpening,
    openDatabaseSettings,
  } = useOpenDatabaseSettingsModal({ allowSystemTypes: true });

  const {
    canPurgeRow,
    confirmMessage,
    confirmOpen,
    deleting,
    finalConfirmOpen,
    handleFinalConfirm,
    handleFirstConfirm,
    openPurgeConfirm,
    setConfirmOpen,
    setFinalConfirmOpen,
  } = useSystemCollectionPurge({ language });

  useEffect(() => {
    if (!baseKey) {
      setDatabases([]);
      return undefined;
    }

    const unsubscribe = subscribeToCollectionsShared(baseKey, (rows) => {
      setDatabases(Array.isArray(rows) ? rows : []);
    });

    return () => unsubscribe?.();
  }, [baseKey]);

  const collectionRows = useMemo(() => (
    buildBaseDatabases({
      databases,
      getType,
      language,
      baseKey,
    })
  ), [baseKey, databases, getType, language, nodeTypesTick]);

  const handleOpenSettings = useCallback((row) => {
    const database = row?.database;
    const typeKey = String(database?.type || row?.type || '').trim();
    if (!database?.key) return;
    if (!canOpenSettingsForType(typeKey)) return;

    openDatabaseSettings({
      databaseKey: database.key,
      database,
      typeKey,
      baseKey: database.baseKey || baseKey,
    });
  }, [baseKey, canOpenSettingsForType, openDatabaseSettings]);

  const handleDeleteCollection = useCallback((row) => {
    const database = row?.database;
    const typeKey = String(database?.type || '').trim();
    const typeDef = typeKey ? getType(typeKey) : null;
    if (!canPurgeRow(typeDef)) return;

    openPurgeConfirm({
      baseKey: database?.baseKey || baseKey,
      typeKey,
      typeDef,
      module: NODE_MODULE.PRIVATE,
    });
  }, [baseKey, canPurgeRow, getType, openPurgeConfirm]);

  return (
    <>
      <SettingsModalPaneHeader
        title="Collections"
        subtitle="Open settings for any collection in this base — including types that are not in the sidebar Menu. System collections can also be cleared from here."
      />

      <SettingsModalPaneContents>
        {collectionRows.length === 0 ? (
          <div
            className="settings-collections-empty"
            style={{ color: theme?.foregroundMuted }}
          >
            No collections in this base yet.
          </div>
        ) : (
          <div className="settings-collections-list">
            {collectionRows.map((row) => {
              const typeKey = String(row?.database?.type || '').trim();
              const typeDef = typeKey ? getType(typeKey) : null;
              const canOpen = canOpenSettingsForType(typeKey);
              const showDelete = canPurgeRow(typeDef) && isSystemTypeData(typeDef);
              const iconResource = typeof row?.iconResource === 'string'
                ? row.iconResource.trim()
                : '';

              return (
                <div
                  key={row.key}
                  className="settings-collections-row"
                >
                  <div className="settings-collections-row-main">
                    <div className="settings-collections-row-icon">
                      {iconResource ? (
                        <img
                          alt=""
                          className="settings-collections-row-icon-resource"
                          draggable={false}
                          src={iconResource}
                        />
                      ) : (
                        <Icon
                          iconKey={row.iconKey || 'deployed_code'}
                          color={theme?.foregroundMuted}
                          width={20}
                          height={20}
                        />
                      )}
                    </div>
                    <div className="settings-collections-row-label">
                      {row.label || typeKey || 'Collection'}
                    </div>
                  </div>

                  <div className="settings-collections-row-actions">
                    {canOpen ? (
                      <Button
                        iconKey="settings"
                        title={`Settings for ${row.label || typeKey}`}
                        height="32px"
                        width="32px"
                        padding="0"
                        iconSize="18px"
                        backgroundColor="transparent"
                        borderColor="transparent"
                        foregroundColor={theme?.foregroundMuted}
                        enabled={!isOpening && !deleting}
                        onClick={() => handleOpenSettings(row)}
                      />
                    ) : null}

                    {showDelete ? (
                      <Button
                        iconKey="delete"
                        title={`Delete ${row.label || typeKey} records from this base`}
                        height="32px"
                        width="32px"
                        padding="0"
                        iconSize="18px"
                        backgroundColor="transparent"
                        borderColor="transparent"
                        foregroundColor={theme?.foregroundMuted}
                        enabled={!deleting}
                        onClick={() => handleDeleteCollection(row)}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SettingsModalPaneContents>

      <DatabaseModal {...databaseModalProps} />

      <Confirm
        title="Delete Collection"
        message={confirmMessage}
        isOpen={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleFirstConfirm}
      />

      <Confirm
        title="Final Confirmation"
        message="Just making sure... we don't want you to accidentally delete anything important to you."
        isOpen={finalConfirmOpen}
        setOpen={setFinalConfirmOpen}
        onConfirm={handleFinalConfirm}
      />
    </>
  );
});

SettingsCollectionsPane.displayName = 'SettingsCollectionsPane';

export default SettingsCollectionsPane;
